import { useState, useCallback, ChangeEvent } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Grid,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  useTheme,
  alpha,
  styled
} from '@mui/material';
import { 
  CloudUpload, 
  CheckCircle, 
  Error, 
  Delete,
  AddCircleOutline,
  Star,
  StarBorder
} from '@mui/icons-material';
import auditService, { AuditEventType, AuditSeverity, ProcessingStage } from '../services/audit/auditService';

// Types for uploaded files
interface UploadedFile {
  id: string;
  file: File;
  uploadStatus: 'idle' | 'uploading' | 'success' | 'error';
  uploadProgress?: number;
}

// Data source configuration
interface DataSourceConfig {
  id: string;
  name: string;
  description: string;
  required: boolean;
  icon: React.ReactNode;
  acceptedTypes: string[];
  maxFiles: number;
}

// Component props
interface DataSourceFileUploaderProps {
  onFilesAdded: (dataSource: string, files: File[]) => void;
  onFileRemoved: (dataSource: string, fileId: string) => void;
  uploadProgress?: Map<string, number>;
  disabled?: boolean;
  maxFilesPerSource?: number;
  clientId?: string;
  workflowId?: string;
}

// Styled components
const StyledDropZone = styled(Paper)(({ theme }) => ({
  border: '2px dashed',
  borderColor: alpha(theme.palette.primary.main, 0.3),
  borderRadius: theme.shape.borderRadius * 2,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
  },
  '&.dragging': {
    borderColor: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '&.disabled': {
    cursor: 'not-allowed',
    opacity: 0.6,
  }
}));

const RequiredChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.error.main, 0.1),
  color: theme.palette.error.main,
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: theme.palette.error.main,
  }
}));

const OptionalChip = styled(Chip)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.info.main, 0.1),
  color: theme.palette.info.main,
  fontWeight: 600,
  '& .MuiChip-icon': {
    color: theme.palette.info.main,
  }
}));

// Data source configurations
const DATA_SOURCES: DataSourceConfig[] = [
  {
    id: 'semrush',
    name: 'SEMrush',
    description: 'Keyword rankings and competition data',
    required: true,
    icon: <Star />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  },
  {
    id: 'gsc',
    name: 'Google Search Console',
    description: 'Search performance and query data',
    required: true,
    icon: <Star />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  },
  {
    id: 'google_ads',
    name: 'Google Ads',
    description: 'Advertising campaign and keyword data',
    required: true,
    icon: <Star />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  },
  {
    id: 'lpm',
    name: 'Google Business Profile Impressions & Actions',
    description: 'Business profile performance data including impressions, clicks, and customer actions',
    required: true,
    icon: <Star />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  },
  {
    id: 'ga4',
    name: 'Google Analytics 4',
    description: 'Website analytics and behavior data',
    required: false,
    icon: <StarBorder />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  },
  {
    id: 'custom',
    name: 'Custom Data',
    description: 'Other data files and custom uploads',
    required: false,
    icon: <StarBorder />,
    acceptedTypes: ['.csv', '.txt'],
    maxFiles: 3
  }
];

export const DataSourceFileUploader: React.FC<DataSourceFileUploaderProps> = ({
  onFilesAdded,
  onFileRemoved,
  uploadProgress = new Map(),
  disabled = false,
  maxFilesPerSource = 3
}) => {
  const theme = useTheme();
  const [uploadedFiles, setUploadedFiles] = useState<Map<string, UploadedFile[]>>(new Map());
  const [dragStates, setDragStates] = useState<Map<string, boolean>>(new Map());

  // Generate unique file ID
  const generateFileId = () => `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Handle drag events for specific data source
  const handleDragEnter = useCallback((dataSourceId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setDragStates(prev => new Map(prev.set(dataSourceId, true)));
    }
  }, [disabled]);

  const handleDragLeave = useCallback((dataSourceId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragStates(prev => new Map(prev.set(dataSourceId, false)));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Handle file drop for specific data source
  const handleDrop = useCallback((dataSourceId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragStates(prev => new Map(prev.set(dataSourceId, false)));
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    handleFilesAdded(dataSourceId, files);
  }, [disabled]);

  // Handle file selection via input
  const handleFileChange = useCallback((dataSourceId: string) => (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled || !e.target.files) return;
    
    const files = Array.from(e.target.files);
    handleFilesAdded(dataSourceId, files);
    
    // Reset input value to allow selecting the same file again
    e.target.value = '';
  }, [disabled]);

  // Process added files
  const handleFilesAdded = useCallback((dataSourceId: string, files: File[]) => {
    const currentFiles = uploadedFiles.get(dataSourceId) || [];
    const dataSource = DATA_SOURCES.find(ds => ds.id === dataSourceId);
    
    if (!dataSource) return;

    // Check file limits
    const availableSlots = Math.max(0, maxFilesPerSource - currentFiles.length);
    const filesToAdd = files.slice(0, availableSlots);
    
    if (filesToAdd.length === 0) {
      return; // No slots available
    }

    // Validate file types
    const validFiles = filesToAdd.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return dataSource.acceptedTypes.includes(extension);
    });

    // Check for duplicates
    const newFiles = validFiles.filter(file => {
      return !currentFiles.some(existing => existing.file.name === file.name);
    });

    if (newFiles.length === 0) return;

    // Create uploaded file objects
    const uploadedFileObjects = newFiles.map(file => ({
      id: generateFileId(),
      file,
      uploadStatus: 'idle' as const
    }));

    // Update state
    setUploadedFiles(prev => {
      const updated = new Map(prev);
      updated.set(dataSourceId, [...currentFiles, ...uploadedFileObjects]);
      return updated;
    });

    // Notify parent component
    onFilesAdded(dataSourceId, newFiles);
  }, [uploadedFiles, maxFilesPerSource, onFilesAdded]);

  // Handle file removal
  const handleFileRemove = useCallback((dataSourceId: string, fileId: string) => {
    setUploadedFiles(prev => {
      const updated = new Map(prev);
      const currentFiles = updated.get(dataSourceId) || [];
      const filteredFiles = currentFiles.filter(f => f.id !== fileId);
      updated.set(dataSourceId, filteredFiles);
      return updated;
    });

    onFileRemoved(dataSourceId, fileId);
  }, [onFileRemoved]);

  // Render individual data source section
  const renderDataSourceSection = (dataSource: DataSourceConfig) => {
    const files = uploadedFiles.get(dataSource.id) || [];
    const isDragging = dragStates.get(dataSource.id) || false;
    const hasFiles = files.length > 0;
    const isAtLimit = files.length >= maxFilesPerSource;

    return (
      <Grid item xs={12} md={6} key={dataSource.id}>
        <Box sx={{ mb: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ mr: 2, fontWeight: 600 }}>
              {dataSource.name}
            </Typography>
            {dataSource.required ? (
              <RequiredChip
                icon={<Star />}
                label="Required"
                size="small"
              />
            ) : (
              <OptionalChip
                icon={<StarBorder />}
                label="Optional"
                size="small"
              />
            )}
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dataSource.description}
          </Typography>

          {/* Drop Zone */}
          <Box
            component="label"
            sx={{ display: 'block', cursor: disabled || isAtLimit ? 'not-allowed' : 'pointer' }}
          >
            <StyledDropZone
              className={`${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
              onDragEnter={handleDragEnter(dataSource.id)}
              onDragLeave={handleDragLeave(dataSource.id)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(dataSource.id)}
              elevation={0}
            >
            <input
              type="file"
              multiple
              accept={dataSource.acceptedTypes.join(',')}
              onChange={handleFileChange(dataSource.id)}
              style={{ display: 'none' }}
              disabled={disabled || isAtLimit}
            />

            <CloudUpload 
              sx={{ 
                fontSize: 32, 
                color: isDragging ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.6),
                mb: 1 
              }} 
            />
            
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 500 }}>
              {isAtLimit 
                ? `Maximum ${maxFilesPerSource} files reached`
                : hasFiles 
                  ? 'Add more files' 
                  : 'Upload files'
              }
            </Typography>
            
            <Typography variant="caption" color="text.secondary">
              {isAtLimit 
                ? 'Remove files to add new ones'
                : `Drag & drop or click to select (${dataSource.acceptedTypes.join(', ')})`
              }
            </Typography>

            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Max {maxFilesPerSource} files • {files.length} uploaded
            </Typography>
          </StyledDropZone>
          </Box>

          {/* File List */}
          {hasFiles && (
            <Paper variant="outlined" sx={{ mt: 2 }}>
              <List dense>
                {files.map((uploadedFile, index) => {
                  const progress = uploadProgress.get(uploadedFile.id);
                  const isUploading = uploadedFile.uploadStatus === 'uploading';
                  const hasError = uploadedFile.uploadStatus === 'error';
                  const isSuccess = uploadedFile.uploadStatus === 'success';

                  return (
                    <ListItem key={uploadedFile.id} divider={index < files.length - 1}>
                      <ListItemText
                        primary={uploadedFile.file.name}
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {(uploadedFile.file.size / 1024).toFixed(1)} KB
                            </Typography>
                            {isUploading && progress !== undefined && (
                              <Typography variant="caption" sx={{ ml: 1 }}>
                                • {Math.round(progress)}% uploaded
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isUploading && (
                            <CircularProgress size={16} />
                          )}
                          {isSuccess && (
                            <CheckCircle sx={{ fontSize: 18, color: 'success.main' }} />
                          )}
                          {hasError && (
                            <Error sx={{ fontSize: 18, color: 'error.main' }} />
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleFileRemove(dataSource.id, uploadedFile.id)}
                            disabled={disabled || isUploading}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}
        </Box>
      </Grid>
    );
  };

  // Get upload summary
  const getUploadSummary = () => {
    const requiredSources = DATA_SOURCES.filter(ds => ds.required);
    const optionalSources = DATA_SOURCES.filter(ds => !ds.required);
    
    const requiredWithFiles = requiredSources.filter(ds => {
      const files = uploadedFiles.get(ds.id) || [];
      return files.length > 0;
    });

    const optionalWithFiles = optionalSources.filter(ds => {
      const files = uploadedFiles.get(ds.id) || [];
      return files.length > 0;
    });

    const totalFiles = Array.from(uploadedFiles.values()).reduce((total, files) => total + files.length, 0);

    return {
      requiredComplete: requiredWithFiles.length,
      requiredTotal: requiredSources.length,
      optionalComplete: optionalWithFiles.length,
      optionalTotal: optionalSources.length,
      totalFiles
    };
  };

  const summary = getUploadSummary();

  return (
    <Box>
      {/* Summary Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Data Source Upload
        </Typography>
        
        <Alert 
          severity={summary.requiredComplete === summary.requiredTotal ? "success" : "info"}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            <strong>Required:</strong> {summary.requiredComplete}/{summary.requiredTotal} sources uploaded • 
            <strong> Optional:</strong> {summary.optionalComplete}/{summary.optionalTotal} sources • 
            <strong> Total files:</strong> {summary.totalFiles}
          </Typography>
        </Alert>
      </Box>

      {/* Data Source Grid */}
      <Grid container spacing={3}>
        {DATA_SOURCES.map(renderDataSourceSection)}
      </Grid>

      {/* Guidelines */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          Upload Guidelines:
        </Typography>
        <Typography variant="body2" color="text.secondary" component="div">
          • Required data sources must have at least one file uploaded
          <br />
          • Maximum {maxFilesPerSource} files per data source
          <br />
          • Supported formats: CSV, TXT files only
          <br />
          • Files will be organized by data source for processing
        </Typography>
      </Box>
    </Box>
  );
};

export default DataSourceFileUploader;
